import React, { useEffect, useState, useMemo, useCallback } from "react";
import { api, formatMoney as formatEuro } from "../lib/api";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
    CartesianGrid,
} from "recharts";
import { X } from "lucide-react";

const STATUS_COLORS = {
    interesado: "#002FA7",
    posible_cierre: "#FF9900",
    ganado: "#008F11",
    perdido: "#D32F2F",
};

// Recharts style constants (extracted to avoid re-renders from inline objects)
const AXIS_TICK = { fontSize: 12 };
const LEGEND_STYLE = { fontSize: 12 };
const TOOLTIP_STYLE = {
    background: "#fff",
    border: "1px solid #0A0A0A",
    borderRadius: 0,
};

const MetricCard = ({ label, value, sub, accent, testId }) => (
    <div
        className="brutal-card p-6 flex flex-col gap-2 relative overflow-hidden"
        data-testid={testId}
    >
        <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: accent }} />
        <span className="label-eyebrow text-neutral-500">{label}</span>
        <span className="font-display text-3xl lg:text-4xl tracking-tight">{value}</span>
        {sub && <span className="text-xs text-neutral-500">{sub}</span>}
    </div>
);

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [agents, setAgents] = useState([]);

    // filters
    const [q, setQ] = useState("");
    const [agentId, setAgentId] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    // Status toggles for "Pipeline por mes" chart
    const [monthStatusFilter, setMonthStatusFilter] = useState({
        interesado: true,
        posible_cierre: true,
        ganado: true,
        perdido: true,
    });
    const toggleMonthStatus = (key) =>
        setMonthStatusFilter((s) => ({ ...s, [key]: !s[key] }));

    const fetchStats = useCallback(async () => {
        setLoading(true);
        const params = {};
        if (q.trim()) params.q = q.trim();
        if (agentId) params.agent_id = agentId;
        if (fromDate) params.from_date = fromDate;
        if (toDate) params.to_date = toDate;
        try {
            const r = await api.get("/admin/stats", { params });
            setStats(r.data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [q, agentId, fromDate, toDate]);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/users", { params: { exclude_admins: true } });
                setAgents(r.data);
            } catch (e) {
                console.error(e);
            }
        })();
    }, []);

    // debounce client search
    useEffect(() => {
        const t = setTimeout(fetchStats, 300);
        return () => clearTimeout(t);
    }, [fetchStats]);

    const clearFilters = () => {
        setQ("");
        setAgentId("");
        setFromDate("");
        setToDate("");
    };
    const hasFilters = q || agentId || fromDate || toDate;

    const derived = useMemo(() => {
        if (!stats) return null;
        const { by_status, by_agent } = stats;
        const totalGanado = by_status.ganado.total;
        const totalPerdido = by_status.perdido.total;
        const totalPipeline =
            by_status.interesado.total + by_status.posible_cierre.total;
        const winRate =
            by_status.ganado.count + by_status.perdido.count > 0
                ? (by_status.ganado.count /
                      (by_status.ganado.count + by_status.perdido.count)) *
                  100
                : 0;
        const barData = by_agent.map((a) => ({
            name: a.owner_name.split(" ")[0],
            Interesado: a.interesado,
            "Cierre 7d": a.posible_cierre,
            Ganado: a.ganado,
            Perdido: a.perdido,
        }));
        const pieData = [
            { name: "Ganados", value: totalGanado, key: "ganado" },
            { name: "Perdidos", value: totalPerdido, key: "perdido" },
        ].filter((d) => d.value > 0);
        const monthLabels = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
        const monthData = (stats.by_month || []).map((m) => {
            const [y, mm] = m.month.split("-");
            return {
                name: `${monthLabels[parseInt(mm, 10) - 1]} ${y.slice(2)}`,
                Interesado: m.interesado,
                "Cierre 7d": m.posible_cierre,
                Ganado: m.ganado,
                Perdido: m.perdido,
            };
        });
        return {
            by_status,
            by_agent,
            totalGanado,
            totalPerdido,
            totalPipeline,
            winRate,
            barData,
            pieData,
            monthData,
        };
    }, [stats]);

    return (
        <div className="px-6 lg:px-10 py-8 space-y-10" data-testid="admin-dashboard">
            <div>
                <span className="label-eyebrow text-neutral-500">Panel Administrador</span>
                <h1 className="font-display text-4xl lg:text-5xl tracking-tight mt-1">
                    Métricas globales
                </h1>
            </div>

            {/* Filters */}
            <div className="brutal-card p-5" data-testid="filters-bar">
                <div className="flex flex-wrap items-end gap-4">
                    <div className="flex-1 min-w-[200px]">
                        <label className="label-eyebrow text-neutral-500 block mb-1">
                            Cliente
                        </label>
                        <input
                            data-testid="filter-client"
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Buscar por nombre…"
                            className="brutal-input"
                        />
                    </div>
                    <div className="min-w-[200px]">
                        <label className="label-eyebrow text-neutral-500 block mb-1">
                            Agente
                        </label>
                        <select
                            data-testid="filter-agent"
                            value={agentId}
                            onChange={(e) => setAgentId(e.target.value)}
                            className="brutal-input cursor-pointer pr-2"
                        >
                            <option value="">Todos</option>
                            {agents.map((a) => (
                                <option key={a.user_id} value={a.user_id}>
                                    {a.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="label-eyebrow text-neutral-500 block mb-1">
                            Llegada desde
                        </label>
                        <input
                            data-testid="filter-from"
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="brutal-input"
                        />
                    </div>
                    <div>
                        <label className="label-eyebrow text-neutral-500 block mb-1">
                            Llegada hasta
                        </label>
                        <input
                            data-testid="filter-to"
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="brutal-input"
                        />
                    </div>
                    {hasFilters && (
                        <button
                            data-testid="filter-clear"
                            onClick={clearFilters}
                            className="brutal-btn-outline px-4 py-2 text-sm flex items-center gap-2 h-[38px]"
                        >
                            <X className="w-3.5 h-3.5" />
                            Limpiar
                        </button>
                    )}
                </div>
            </div>

            {loading || !derived ? (
                <div className="text-sm text-neutral-500" data-testid="loading">
                    Cargando…
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <MetricCard
                            testId="metric-pipeline"
                            label="Pipeline activo"
                            value={formatEuro(derived.totalPipeline)}
                            sub={`${
                                derived.by_status.interesado.count +
                                derived.by_status.posible_cierre.count
                            } oportunidades`}
                            accent="#002FA7"
                        />
                        <MetricCard
                            testId="metric-ganado"
                            label="Ganados"
                            value={formatEuro(derived.totalGanado)}
                            sub={`${derived.by_status.ganado.count} cierres`}
                            accent="#008F11"
                        />
                        <MetricCard
                            testId="metric-perdido"
                            label="Perdidos"
                            value={formatEuro(derived.totalPerdido)}
                            sub={`${derived.by_status.perdido.count} oportunidades`}
                            accent="#D32F2F"
                        />
                        <MetricCard
                            testId="metric-winrate"
                            label="Tasa de éxito"
                            value={`${derived.winRate.toFixed(0)}%`}
                            sub="Ganados / (Ganados + Perdidos)"
                            accent="#0A0A0A"
                        />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="brutal-card p-6 lg:col-span-2" data-testid="chart-by-agent">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="font-display text-xl">Ventas por agente</h2>
                                <span className="label-eyebrow text-neutral-500">$ por estado</span>
                            </div>
                            {derived.barData.length === 0 ? (
                                <p className="text-sm text-neutral-500 py-12 text-center">
                                    Sin datos para este filtro.
                                </p>
                            ) : (
                                <ResponsiveContainer width="100%" height={320}>
                                    <BarChart data={derived.barData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                                        <XAxis dataKey="name" tick={AXIS_TICK} />
                                        <YAxis tick={AXIS_TICK} />
                                        <Tooltip
                                            contentStyle={TOOLTIP_STYLE}
                                            formatter={(v) => formatEuro(v)}
                                        />
                                        <Legend wrapperStyle={LEGEND_STYLE} />
                                        <Bar dataKey="Interesado" fill={STATUS_COLORS.interesado} />
                                        <Bar dataKey="Cierre 7d" fill={STATUS_COLORS.posible_cierre} />
                                        <Bar dataKey="Ganado" fill={STATUS_COLORS.ganado} />
                                        <Bar dataKey="Perdido" fill={STATUS_COLORS.perdido} />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                        <div className="brutal-card p-6" data-testid="chart-pie">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="font-display text-xl">Ganados vs Perdidos</h2>
                            </div>
                            {derived.pieData.length === 0 ? (
                                <p className="text-sm text-neutral-500 py-12 text-center">
                                    Sin datos para este filtro.
                                </p>
                            ) : (
                                <ResponsiveContainer width="100%" height={320}>
                                    <PieChart>
                                        <Pie
                                            data={derived.pieData}
                                            dataKey="value"
                                            nameKey="name"
                                            outerRadius={100}
                                            innerRadius={60}
                                            label={(d) => formatEuro(d.value)}
                                        >
                                            {derived.pieData.map((d) => (
                                                <Cell key={d.key} fill={STATUS_COLORS[d.key]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(v) => formatEuro(v)} />
                                        <Legend wrapperStyle={LEGEND_STYLE} />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>

                    <div className="brutal-card p-6" data-testid="chart-by-month">
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                            <h2 className="font-display text-xl">Pipeline por mes de llegada</h2>
                            <div className="flex flex-wrap items-center gap-2">
                                {[
                                    { key: "interesado", label: "Interesado", color: STATUS_COLORS.interesado },
                                    { key: "posible_cierre", label: "Cierre 7d", color: STATUS_COLORS.posible_cierre },
                                    { key: "ganado", label: "Ganado", color: STATUS_COLORS.ganado },
                                    { key: "perdido", label: "Perdido", color: STATUS_COLORS.perdido },
                                ].map((s) => {
                                    const active = monthStatusFilter[s.key];
                                    return (
                                        <button
                                            key={s.key}
                                            data-testid={`toggle-month-${s.key}`}
                                            onClick={() => toggleMonthStatus(s.key)}
                                            className={`flex items-center gap-2 px-3 py-1.5 text-xs border transition-colors ${
                                                active
                                                    ? "border-black bg-black text-white"
                                                    : "border-black/20 text-neutral-500 hover:border-black"
                                            }`}
                                        >
                                            <span
                                                className="w-2 h-2 inline-block"
                                                style={{ background: active ? s.color : "#bbb" }}
                                            />
                                            {s.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        {derived.monthData.length === 0 ? (
                            <p className="text-sm text-neutral-500 py-12 text-center">
                                Sin datos para este filtro.
                            </p>
                        ) : (
                            <ResponsiveContainer width="100%" height={340}>
                                <BarChart data={derived.monthData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                                    <XAxis dataKey="name" tick={AXIS_TICK} />
                                    <YAxis tick={AXIS_TICK} tickFormatter={(v) => v >= 1000 ? `$${(v/1000).toFixed(0)}k` : `$${v}`} />
                                    <Tooltip
                                        contentStyle={TOOLTIP_STYLE}
                                        formatter={(v) => formatEuro(v)}
                                    />
                                    {monthStatusFilter.interesado && (
                                        <Bar dataKey="Interesado" stackId="a" fill={STATUS_COLORS.interesado} />
                                    )}
                                    {monthStatusFilter.posible_cierre && (
                                        <Bar dataKey="Cierre 7d" stackId="a" fill={STATUS_COLORS.posible_cierre} />
                                    )}
                                    {monthStatusFilter.ganado && (
                                        <Bar dataKey="Ganado" stackId="a" fill={STATUS_COLORS.ganado} />
                                    )}
                                    {monthStatusFilter.perdido && (
                                        <Bar dataKey="Perdido" stackId="a" fill={STATUS_COLORS.perdido} />
                                    )}
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    <div className="brutal-card p-6" data-testid="agent-table">
                        <h2 className="font-display text-xl mb-6">Detalle por agente</h2>
                        {derived.by_agent.length === 0 ? (
                            <p className="text-sm text-neutral-500 py-8 text-center">
                                Sin agentes para este filtro.
                            </p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="border-b border-black/15">
                                        <tr className="text-left">
                                            <th className="py-3 label-eyebrow text-neutral-500">Agente</th>
                                            <th className="py-3 label-eyebrow text-neutral-500 text-right">Interesado</th>
                                            <th className="py-3 label-eyebrow text-neutral-500 text-right">Cierre 7d</th>
                                            <th className="py-3 label-eyebrow text-neutral-500 text-right">Ganado</th>
                                            <th className="py-3 label-eyebrow text-neutral-500 text-right">Perdido</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {derived.by_agent.map((a) => (
                                            <tr
                                                key={a.owner_id}
                                                className="border-b border-black/10"
                                                data-testid={`agent-row-${a.owner_id}`}
                                            >
                                                <td className="py-3 font-medium">{a.owner_name}</td>
                                                <td className="py-3 text-right">{formatEuro(a.interesado)}</td>
                                                <td className="py-3 text-right">{formatEuro(a.posible_cierre)}</td>
                                                <td className="py-3 text-right text-[#008F11]">{formatEuro(a.ganado)}</td>
                                                <td className="py-3 text-right text-[#D32F2F]">{formatEuro(a.perdido)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
